import type { Species, SpeciesCategory } from '../lore'
import { Markdown } from './Markdown'

// category grouping order + labels (only used when species have a category set)
const CATEGORY_ORDER: SpeciesCategory[] = ['aerial', 'land', 'ocean', 'unconfirmed']
const categoryLabel: Record<SpeciesCategory, string> = {
  aerial: 'Aerial',
  land: 'Land',
  ocean: 'Ocean',
  unconfirmed: 'Unconfirmed',
}

function SpeciesItem({ s }: { s: Species }) {
  return (
    <li id={`fauna-${s.id}`} className="py-4 first:pt-0 last:pb-0 scroll-mt-24">
      <div className="font-display text-white">{s.name}</div>
      {s.tagline && <div className="mt-1 text-xs text-slate-400">{s.tagline}</div>}
      <div className="md mt-2 text-sm">
        <Markdown>{s.lore}</Markdown>
      </div>
    </li>
  )
}

/**
 * A vertical list of species entries (fauna). Reused by panel + detail page.
 * When any species carries a `category`, entries are grouped under category
 * headings (Aerial / Land / Ocean / Unconfirmed); otherwise rendered flat.
 */
export function SpeciesList({ species }: { species: Species[] }) {
  const hasCategories = species.some((s) => s.category)

  if (!hasCategories) {
    return (
      <ul className="divide-y divide-edge">
        {species.map((s) => (
          <SpeciesItem key={s.id} s={s} />
        ))}
      </ul>
    )
  }

  return (
    <div className="space-y-5">
      {CATEGORY_ORDER.map((cat) => {
        const items = species.filter((s) => s.category === cat)
        if (!items.length) return null
        return (
          <div key={cat}>
            <div className="mb-3 text-sm font-medium uppercase tracking-[0.22em] text-slate-300">
              {categoryLabel[cat]}
            </div>
            <ul className="divide-y divide-edge">
              {items.map((s) => (
                <SpeciesItem key={s.id} s={s} />
              ))}
            </ul>
          </div>
        )
      })}
    </div>
  )
}
